/**
 * KYB workspace for one company (moderator).
 *
 * Left: step 2 (choose a provider, run a check) and step 3 (each check with
 * its registry result, comparison, hits and decision). Right: the evidence
 * facts and step 4 (award a badge), then monitoring and the compliance values
 * confirmed checks have set. The stepper on top shows where the company is.
 *
 * Data: `companies/<id>/` (checks, monitoring, field sources),
 * `companies/<id>/badges/` (verdict, evidence, badges, awards) and the queue
 * row (registrations). Running checks are polled every 10 s.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { unwrapPaginated } from "@/config/api";
import { EMPTY_VALUE, formatCurrency, formatDate } from "@/i18n/format";
import { flowStates, isCheckInFlight, VERDICT_CHIP } from "@/lib/kyb";
import type {
  CompanyBadgePanel,
  CompanyVerificationOverview,
  VerificationComplianceValues,
  VerificationFieldEnum,
  VerificationFieldSource,
  VerificationQueueRow,
} from "@/services/moderators/generated/model";
import {
  getVerificationModeratorChecksListQueryKey,
  getVerificationModeratorCompaniesBadgesRetrieveQueryKey,
  getVerificationModeratorCompaniesListQueryKey,
  getVerificationModeratorCompaniesRetrieveQueryKey,
  getVerificationModeratorProvidersListQueryKey,
  useVerificationModeratorCompaniesBadgesRetrieve,
  useVerificationModeratorCompaniesList,
  useVerificationModeratorCompaniesMonitoringCreate,
  useVerificationModeratorCompaniesRetrieve,
} from "@/services/moderators/generated/moderators-verification/moderators-verification";
import { KybBadgePanel } from "./KybBadgePanel";
import { KybCheckCard } from "./KybCheckCard";
import { KybFlowSteps } from "./KybFlowSteps";
import { KybProviderPicker } from "./KybProviderPicker";
import "../ModeratorPage.css";
import "./Kyb.css";

const POLL_MS = 10_000;

const PROVIDER_FIELD_ORDER: VerificationFieldEnum[] = [
  "tier1BasicVerification",
  "tier2StandardCompliance",
  "tier3AdvancedDueDiligence",
  "pepMatches",
  "amlAlerts",
  "lastScreening",
  "overallComplianceStatus",
  "overallComplianceScore",
];

const ModeratorKybCompanyPage = () => (
  <ModeratorLayout>{(moderator) => <Content isSuper={moderator.role === "SUPERMODERATOR"} />}</ModeratorLayout>
);

const Content = ({ isSuper }: { isSuper: boolean }) => {
  const { t } = useTranslation(["kyb", "common"]);
  const { id } = useParams<{ id: string }>();
  const companyId = Number(id);
  const queryClient = useQueryClient();

  const overviewQuery = useVerificationModeratorCompaniesRetrieve(companyId, {
    query: {
      enabled: Number.isFinite(companyId),
      refetchInterval: (q) =>
        (q.state.data as CompanyVerificationOverview | undefined)?.checks.some(isCheckInFlight) ? POLL_MS : false,
    },
  });
  const panelQuery = useVerificationModeratorCompaniesBadgesRetrieve(companyId, {
    query: { enabled: Number.isFinite(companyId) },
  });
  const overview = overviewQuery.data as CompanyVerificationOverview | undefined;
  const panel = panelQuery.data as CompanyBadgePanel | undefined;

  // Registrations come with the queue row; search by name, then match the id.
  const queueQuery = useVerificationModeratorCompaniesList(
    { search: overview?.companyName ?? "", page_size: 100 },
    { query: { enabled: Boolean(overview?.companyName) } },
  );
  const row = useMemo(() => {
    const rows: VerificationQueueRow[] = unwrapPaginated(queueQuery.data);
    return rows.find((r) => r.companyId === companyId);
  }, [queueQuery.data, companyId]);

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: getVerificationModeratorCompaniesRetrieveQueryKey(companyId) });
    queryClient.invalidateQueries({ queryKey: getVerificationModeratorCompaniesBadgesRetrieveQueryKey(companyId) });
    queryClient.invalidateQueries({ queryKey: getVerificationModeratorCompaniesListQueryKey() });
    queryClient.invalidateQueries({ queryKey: getVerificationModeratorChecksListQueryKey() });
    queryClient.invalidateQueries({ queryKey: getVerificationModeratorProvidersListQueryKey() });
  };

  if (!Number.isFinite(companyId)) return <div className="mod-page__error">{t("company.invalidId")}</div>;
  if (overviewQuery.isLoading) return <div className="mod-page__loading">{t("common:states.loading")}</div>;
  if (overviewQuery.error || !overview) return <div className="mod-page__error">{t("company.loadError")}</div>;

  const registrations = row?.registrations ?? [];
  const steps = flowStates({
    hasRegistration: registrations.length > 0,
    checks: overview.checks,
    hasPublicBadge: Boolean(panel?.publicAward),
  });
  const kybChecks = overview.checks.filter((c) => c.kind === "kyb");
  const monitoringChecks = overview.checks.filter((c) => c.kind === "monitoring");

  return (
    <div>
      <div className="mod-page__toolbar">
        <Link to="/moderator/kyb" className="mod-page__btn mod-page__btn--ghost">
          {t("company.back")}
        </Link>
      </div>
      <header className="mod-page__header">
        <div>
          <h1 className="mod-page__title">{overview.companyName}</h1>
          <p className="mod-page__subtitle">
            {panel ? (
              <span className={`mod-page__chip ${VERDICT_CHIP[panel.verdict]}`}>
                {t("verdict.prefix", { verdict: t(`verdict.${panel.verdict}`) })}
              </span>
            ) : null}
            {panel?.publicAward ? (
              <span className="kyb-badge">
                <i className="fa-solid fa-shield-halved" /> {panel.publicAward.badgeLabel}
              </span>
            ) : (
              <span className="kyb-badge kyb-badge--none">{t("badges.noPublicBadge")}</span>
            )}
          </p>
          <p className="mod-page__subtitle">
            {t("company.spend", {
              spent: formatCurrency(overview.totalCost, overview.costCurrency),
              budget: formatCurrency(overview.budgetPerCompany, overview.costCurrency),
            })}
          </p>
        </div>
        <Link to={`/moderator/companies/${companyId}`} className="mod-page__btn mod-page__btn--ghost">
          {t("company.profile")}
        </Link>
      </header>

      <KybFlowSteps states={steps} />

      <div className="kyb-grid">
        <div>
          <section className="kyb-section">
            <h2 className="kyb-section__title">
              <span className="mod-page__chip">1</span> {t("company.registrationsTitle")}
            </h2>
            <p className="kyb-section__help">{t("company.registrationsHelp")}</p>
            {registrations.length === 0 ? (
              <div className="kyb-callout kyb-callout--warning">{t("providers.noRegistration")}</div>
            ) : (
              <table className="kyb-table">
                <tbody>
                  {registrations.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <span className="mod-page__chip">{r.subdivision || r.country}</span>
                      </td>
                      <td>{r.registrationNumber}</td>
                      <td>
                        <span className={`mod-page__chip ${r.status === "active" ? "mod-page__chip--success" : ""}`}>
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {registrations.some((r) => !r.subdivision) ? (
              <p className="kyb-note">{t("company.subdivisionHint")}</p>
            ) : null}
          </section>

          <KybProviderPicker
            key={registrations.map((r) => r.id).join(",")}
            companyId={companyId}
            registrations={registrations}
            onStarted={refreshAll}
          />

          <section>
            <h2 className="kyb-section__title">
              <span className="mod-page__chip">3</span> {t("company.checksTitle", { count: kybChecks.length })}
            </h2>
            <p className="kyb-section__help">{t("company.checksHelp")}</p>
            {kybChecks.length === 0 ? <div className="mod-page__empty">{t("company.noChecks")}</div> : null}
            {kybChecks.map((check) => (
              <KybCheckCard key={check.id} check={check} isSuper={isSuper} onChanged={refreshAll} />
            ))}
          </section>
        </div>

        <div>
          {panel ? <KybBadgePanel panel={panel} onChanged={refreshAll} /> : null}
          {panelQuery.error ? <div className="mod-page__error">{t("badges.loadError")}</div> : null}

          <MonitoringCard companyId={companyId} overview={overview} isSuper={isSuper} onChanged={refreshAll} />
          {monitoringChecks.map((check) => (
            <KybCheckCard key={check.id} check={check} isSuper={isSuper} onChanged={refreshAll} />
          ))}
          <FieldSourcesCard current={overview.current} sources={overview.fieldSources} />
        </div>
      </div>
    </div>
  );
};

const MonitoringCard = ({
  companyId,
  overview,
  isSuper,
  onChanged,
}: {
  companyId: number;
  overview: CompanyVerificationOverview;
  isSuper: boolean;
  onChanged: () => void;
}) => {
  const { t } = useTranslation("kyb");
  const toggle = useVerificationModeratorCompaniesMonitoringCreate({ mutation: { onSuccess: onChanged } });
  const active = overview.monitoring.active;
  return (
    <section className="kyb-section">
      <h2 className="kyb-section__title">
        {t("monitoring.title")}
        <span className={`mod-page__chip ${active ? "mod-page__chip--success" : ""}`}>
          {active ? t("monitoring.on") : t("monitoring.off")}
        </span>
      </h2>
      <p className="kyb-section__help">
        {t("monitoring.help", { provider: overview.provider })}
        {overview.monitoring.subscribedAt
          ? ` ${t("monitoring.since", { date: formatDate(overview.monitoring.subscribedAt) })}`
          : ""}
      </p>
      {isSuper ? (
        <button
          type="button"
          className={`mod-page__btn ${active ? "mod-page__btn--ghost" : "mod-page__btn--primary"}`}
          disabled={toggle.isPending}
          onClick={() => toggle.mutate({ id: companyId, data: { active: !active } })}
        >
          {active ? t("monitoring.turnOff") : t("monitoring.turnOn")}
        </button>
      ) : (
        <p className="kyb-note">{t("monitoring.superOnly")}</p>
      )}
    </section>
  );
};

const FieldSourcesCard = ({
  current,
  sources,
}: {
  current: VerificationComplianceValues;
  sources: VerificationFieldSource[];
}) => {
  const { t } = useTranslation("kyb");
  const byField = new Map(sources.map((s) => [s.field, s]));
  return (
    <section className="kyb-section">
      <h2 className="kyb-section__title">{t("fields.title")}</h2>
      <p className="kyb-section__help">{t("fields.help")}</p>
      <table className="kyb-table">
        <tbody>
          {PROVIDER_FIELD_ORDER.map((field) => {
            const value = (current as Record<string, number | string | null | undefined>)[field];
            const source = byField.get(field);
            const sourceKey = source?.source === "provider" || source?.source === "manual" ? source.source : "unset";
            return (
              <tr key={field}>
                <td>{t(`field.${field}`)}</td>
                <td>{value === null || value === undefined || value === "" ? EMPTY_VALUE : String(value)}</td>
                <td>
                  <span className="mod-page__chip">{t(`fields.source.${sourceKey}`)}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
};

export default ModeratorKybCompanyPage;
